import { css, DateRangePicker, FormField, Inline, SelectInput, styled } from 'canoe';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  AnalyticsQueryParams,
  TimeIntervalDropdown,
  timeIntervalOptions,
} from '@candu-ai/analytics/types';
import {
  createMinMaxDates,
  toEndOfDayTimestamp,
  toSecondTimeStamp,
} from '@candu-ai/analytics/utils';

import { setDateRange, setShortTimeInterval, useAnalyticsSearchState } from './state';
import { asUtc, toUtc } from './utcConversions';

const TimeIntervalFilter = styled.div`
  ${({ theme }) => css`
    width: 200px;
    display: inline-block;
    padding: ${theme.spaces.xxs} 0;
  `}
`;

export const TimeFilter = React.memo(() => {
  const { shortTimeInterval, dateRange } = useAnalyticsSearchState();
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const onSearchParamChange = useCallback(
    (params: { key: string; value?: string }[]) => {
      params.forEach(({ key, value }) => {
        if (!value || value === '') {
          if (searchParams.has(key)) {
            searchParams.delete(key);
            setSearchParams(searchParams, { replace: true });
          }
        } else {
          searchParams.set(key, value);
          setSearchParams(searchParams, { replace: true });
        }
      });
    },
    [searchParams],
  );
  const onIntervalChange = useCallback(
    (selectedItem) => {
      if (selectedItem.value === 'custom' && /^\d+\w$/.test(shortTimeInterval)) {
        const copyOfShortTimeInterval = shortTimeInterval;
        const period = copyOfShortTimeInterval.slice(-1);
        const duration = parseInt(copyOfShortTimeInterval, 10);

        const minMaxDates = createMinMaxDates({ duration, period }, { duration: 0, period });

        onSearchParamChange([
          { key: AnalyticsQueryParams.Timestamp, value: toSecondTimeStamp(minMaxDates[0]) },
          { key: AnalyticsQueryParams.UpperTimestamp, value: toEndOfDayTimestamp(minMaxDates[1]) },
        ]);
        dispatch(setDateRange(minMaxDates));
      }
      if (selectedItem) {
        onSearchParamChange([
          { key: AnalyticsQueryParams.TimeInterval, value: selectedItem.value },
        ]);
        dispatch(setShortTimeInterval(selectedItem.value));
      }
    },
    [onSearchParamChange],
  );

  return (
    <Inline align="center">
      <TimeIntervalFilter>
        <SelectInput<TimeIntervalDropdown>
          onSelect={onIntervalChange}
          selectedItem={timeIntervalOptions.find((i) => i.value === shortTimeInterval)}
          items={timeIntervalOptions}
          clearable={false}
          listMaxHeight={340}
        />
      </TimeIntervalFilter>
      {shortTimeInterval === 'custom' && dateRange && (
        <FormField label="" marginBottom="xxs">
          <DateRangePicker
            startDate={asUtc(dateRange[0])}
            endDate={asUtc(dateRange[1])}
            dateRangeLimit={30 * 6}
            onConfirm={(state) =>
              dispatch(setDateRange([toUtc(state.startDate), toUtc(state.endDate)]))
            }
          />
        </FormField>
      )}
    </Inline>
  );
});

TimeFilter.displayName = 'TimeFilter';
